import React, { createContext, useCallback, useContext, useState } from 'react';
import { useEvents } from '../../../providers/events';
import { useStateMethods } from '../../../providers/stateManager';
import { stateKeys } from '../../../constants/stateKeys';

const LoadingContext = createContext();
const SetLoadingContext = createContext();
const useLoading = () => useContext(LoadingContext);
const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);

  return (
    <LoadingContext.Provider value={loading}>
      <SetLoadingContext.Provider value={setLoading}>
        {children}
      </SetLoadingContext.Provider>
    </LoadingContext.Provider>
  );
};

const EditContext = createContext();
const EditCommentProvider = ({ children }) => {
  const setLoading = useContext(SetLoadingContext);

  const { onEditComment } = useEvents();

  const { getValue, reset } = useStateMethods();

  const onSubmit = useCallback(async () => {
    setLoading(true);
    await onEditComment({
      comment: getValue(stateKeys.text).trim(),
      target: getValue(stateKeys.editComment),
      attachments: [
        ...getValue(stateKeys.images),
        ...getValue(stateKeys.files),
      ],
    });
    setLoading(false);
    reset((prev) => ({
      ...prev,
      [stateKeys.text]: '',
      [stateKeys.editComment]: undefined,
      [stateKeys.files]: [],
      [stateKeys.images]: [],
    }));
  }, [onEditComment, setLoading, getValue, reset]);

  return (
    <EditContext.Provider value={onSubmit}>{children}</EditContext.Provider>
  );
};

const AddContext = createContext();
const AddCommentProvider = ({ children }) => {
  const setLoading = useContext(SetLoadingContext);

  const { onAddComment } = useEvents();

  const { getValue, reset } = useStateMethods();

  const onSubmit = useCallback(async () => {
    setLoading(true);
    await onAddComment({
      comment: getValue(stateKeys.text).trim(),
      attachments: [
        ...getValue(stateKeys.images),
        ...getValue(stateKeys.files),
      ],
    });
    reset((prev) => ({
      ...prev,
      [stateKeys.text]: '',
      [stateKeys.files]: [],
      [stateKeys.images]: [],
    }));
    setLoading(false);
  }, [onAddComment, setLoading, getValue, reset]);

  return <AddContext.Provider value={onSubmit}>{children}</AddContext.Provider>;
};

const SubmitContext = createContext();
const useSubmitContext = () => useContext(SubmitContext);
const SubmitContextProvider = ({ children, hasEditComment }) => {
  return (
    <SubmitContext.Provider value={hasEditComment ? EditContext : AddContext}>
      {children}
    </SubmitContext.Provider>
  );
};

const Provider = ({ children, hasEditComment }) => {
  const SubmitProvider = hasEditComment
    ? EditCommentProvider
    : AddCommentProvider;

  return (
    <LoadingProvider>
      <SubmitContextProvider hasEditComment={hasEditComment}>
        <SubmitProvider>{children}</SubmitProvider>
      </SubmitContextProvider>
    </LoadingProvider>
  );
};

export { Provider, useLoading, useSubmitContext };
